import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { teamsAPI } from '../api/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Apply = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gamertag: '',
    platform: '',
    preferredTeamId: '',
    experience: '',
    reason: '',
    availability: ''
  });

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      // Filter out teams that already have owners
      const availableTeams = response.data.filter(team => !team.owner);
      setTeams(availableTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/applications`, {
        ...formData,
        preferredTeamId: formData.preferredTeamId || null
      });

      setSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        gamertag: '',
        platform: '',
        preferredTeamId: '',
        experience: '',
        reason: '',
        availability: ''
      });

    } catch (err) {
      setError(err.response?.data?.error || t('apply.submitError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('apply.applicationSubmitted')} 🎉
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
              {t('apply.thankYou')}
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">{t('apply.whatHappensNext')}</h3>
              <ul className="text-sm text-blue-800 space-y-2 text-left">
                <li>✅ {t('apply.applicationReceived')}</li>
                <li>⏳ {t('apply.adminReview')}</li>
                <li>📧 {t('apply.inviteCodeEmail')}</li>
                <li>🎮 {t('apply.useCodeToRegister')}</li>
              </ul>
            </div>
            <div className="flex gap-4 justify-center">
              <Link 
                to="/" 
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
              >
                {t('apply.backToHome')}
              </Link>
              <Link 
                to="/teams" 
                className="bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
              >
                {t('apply.browseTeams')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-700 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://www.2kbundesliga.de/wp-content/uploads/2022/01/cropped-cropped-2kbuli-header_06.png" 
              alt="2K Bundesliga Logo"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('apply.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('apply.subtitle')}
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg">
            <div className="text-3xl mb-2">🏀</div>
            <h3 className="font-bold">{t('apply.becomeTeamOwner')}</h3>
            <p className="text-sm text-blue-100 mt-1">{t('apply.controlTeam')}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg">
            <div className="text-3xl mb-2">🎮</div>
            <h3 className="font-bold">{t('apply.xboxPlayStation')}</h3>
            <p className="text-sm text-green-100 mt-1">{t('apply.crossPlatform')}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg">
            <div className="text-3xl mb-2">🏆</div>
            <h3 className="font-bold">{t('apply.competeGlory')}</h3>
            <p className="text-sm text-purple-100 mt-1">{t('apply.championship')}</p>
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b">
                {t('apply.personalInfo')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.firstName')} *
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.firstNamePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.lastName')} *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.lastNamePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Gaming Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b">
                {t('apply.gamingInfo')}
              </h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="gamertag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.gamertag')} *
                  </label>
                  <input
                    id="gamertag"
                    name="gamertag"
                    type="text"
                    required
                    value={formData.gamertag}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.gamertagPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.platform')} *
                  </label>
                  <select
                    id="platform"
                    name="platform"
                    required
                    value={formData.platform}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  >
                    <option value="">{t('apply.selectPlatform')}</option>
                    <option value="Xbox">🎮 {t('apply.xboxSeries')}</option>
                    <option value="PlayStation">🎯 {t('apply.playStation5')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.email')} *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.emailPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('apply.emailNote')}
                  </p>
                </div>
              </div>
            </div>

            {/* Team Preference */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b">
                {t('apply.teamPreference')}
              </h2>
              <div>
                <label htmlFor="preferredTeamId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('apply.preferredTeam')}
                </label>
                <select
                  id="preferredTeamId"
                  name="preferredTeamId"
                  value={formData.preferredTeamId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                >
                  <option value="">{t('apply.noPreference')}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.city} {team.name} ({team.abbreviation})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('apply.teamNote')}
                </p>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b">
                {t('apply.additionalInfo')}
              </h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.experience')}
                  </label>
                  <textarea
                    id="experience"
                    name="experience"
                    rows="3"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.experiencePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.reason')}
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    rows="3"
                    value={formData.reason}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.reasonPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="availability" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('apply.availability')}
                  </label>
                  <input
                    id="availability"
                    name="availability"
                    type="text"
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder={t('apply.availabilityPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold text-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                {loading ? t('apply.submittingApplication') : t('apply.submitApplication')}
              </button>
              <p className="mt-3 text-center text-sm text-gray-600 dark:text-gray-400">
                {t('apply.alreadyHaveCode')} <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">{t('apply.registerHere')}</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Apply;
